import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom, Observable } from 'rxjs';

@Component({
    selector: 'app-pedidos',
    templateUrl: './pedidos.component.html',
    styleUrls: ['./pedidos.component.css'],
    standalone: false
})
export class PedidosComponent {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private _orderComercioService = inject(OrderComercioService);
  private router = inject(Router);
  private _location = inject(Location);
  private aRoute = inject(ActivatedRoute);

  error = signal<string>('');
  message = signal<string>('');
  
  // tooltip de ayuda (reemplaza a getElementById)
  helpVisible = signal<boolean>(false);
  helpText = signal<string>('');

  // datos
  id_perfil?: number;
  id_estado_orden: number | null;

  ordenesComercioAnteriores: OrdenComercio[] = [];
  ordenesComercioCurso: OrdenComercio[] = [];
  ordenesComercioFiltradas: OrdenComercio[] = [];

  // estados (ajustá si cambian)
  private static readonly ESTADOS_ANTERIORES = new Set([4, 5, 9, 10]);
  private static readonly ESTADOS_EN_CURSO   = new Set([1, 2, 3, 6, 7, 8, 11]);

  constructor(){
    const raw = this.aRoute.snapshot.paramMap.get('id_estado_orden');
    const parsed = raw != null ? Number(raw) : NaN;
    this.id_estado_orden = Number.isFinite(parsed) ? parsed : null;
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession();

        // 2) Perfil
        const u = this._usuarioService.usuario();
        this.id_perfil = u?.id_perfil ?? undefined;
        const userId = u?.id!;
        if (!this.id_perfil || !userId) throw new Error('No se encontró usuario/perfil');

        // 3) Traer órdenes según perfil
        const ordenes = await firstValueFrom(this.obtenerOrdenesPorPerfil(this.id_perfil, userId));

        // 4) Filtrar por estado si viene en la URL; si no, dividir en "curso" / "anteriores"
        const isNumber = (v: unknown): v is number => typeof v === 'number';
        if (this.id_estado_orden == null) {
          this.ordenesComercioAnteriores = ordenes.filter(o =>
            isNumber(o.id_estado_orden) && PedidosComponent.ESTADOS_ANTERIORES.has(o.id_estado_orden)
          );
          this.ordenesComercioCurso = ordenes.filter(o =>
            isNumber(o.id_estado_orden) && PedidosComponent.ESTADOS_EN_CURSO.has(o.id_estado_orden)
          );
        } else {
          this.ordenesComercioFiltradas = ordenes.filter(o =>
            isNumber(o.id_estado_orden) && o.id_estado_orden === this.id_estado_orden
          );
        }
      });
    } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar tu cuenta.';
        this.router.navigate(['/sign-in']);
    }
  }

  /** Selecciona el endpoint correcto según el perfil */
  private obtenerOrdenesPorPerfil(perfil: number, userId: string): Observable<OrdenComercio[]> {
    // ADMIN(1) y COMERCIO(4) → por comercio | CLIENTE(2) y PRESTADOR(3) → por usuario
    if (perfil === 1 || perfil === 4) {
      return this._orderComercioService.getOrdenesComercioXComercio(userId);
    }
    return this._orderComercioService.getOrdenesComercioXUsuario(userId);
  }

  back(){
    this._location.back();
  }

  /** Toggle del tooltip de ayuda con texto según perfil */
  toggleHelp() {
    if (this.helpVisible()) {
      this.helpVisible.set(false);
      return;
    }

    let txt = 'INFO general';
    switch (this.id_perfil) {
      case 1: txt = 'Podrás encontrar todas las compras que realizaste.'; break; // ADMIN
      case 2: txt = 'Podrás encontrar todas las compras que realizaste.'; break; // CLIENTE
      case 3: txt = 'Podrás encontrar todas las compras que realizaste.'; break; // PRESTADOR
      case 4: txt = 'Podrás encontrar todas las compras que realizaste.'; break; // COMERCIO
      default: txt = 'Podrás encontrar todas las compras que realizaste.'; break;
    }
    this.helpText.set(txt);
    this.helpVisible.set(true);
  }
  
}
